import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button/new';
import GrowHorizontalIcon from '@atlaskit/icon/core/grow-horizontal';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { Flex } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import {
	ContentAppearancePropertyKey,
	ContentAppearanceType,
} from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { updateContentAppearancePropertyWithRetry } from '@confluence/content-appearance/entry-points/contentAppearance.ts';
import { ContentAppearanceButton } from '@confluence/content-appearance/entry-points/ContentAppearanceButton.ts';

import type {
	EditContentAppearanceButtonProps,
	UpdateContentAppearancePropertyBody,
} from './editorAppearanceTypes.ts';

const messages = defineMessages({
	adjustWidthButtonLabel: {
		id: 'editor-appearance-button.max-width.adjust-width-label',
		defaultMessage: 'Adjust width',
		description: 'Label for the adjust width button that opens the appearance dropdown',
	},
	fullPageLabel: {
		id: 'editor-appearance-button.max-width.narrow-label',
		defaultMessage: 'Narrow',
		description: 'Label for narrow appearance option',
	},
	fullWidthLabel: {
		id: 'editor-appearance-button.max-width.wide-label',
		defaultMessage: 'Wide',
		description: 'Label for wide appearance option',
	},
	maxLabel: {
		id: 'editor-appearance-button.max-width.max-label',
		defaultMessage: 'Max',
		description: 'Label for max width appearance option',
	},
	dropdownAriaLabel: {
		id: 'editor-appearance-button.max-width.dropdown-aria-label',
		defaultMessage: 'Page width options',
		description: 'Aria label for the page width dropdown menu',
	},
});

/**
 * This container with "relative" positioning
 * is necessary for the top border on scroll to
 * correctly anchor itself
 */
const styles = cssMap({
	dropdownButtonFlexGroupStyles: {
		height: '20px',
	},
});

export type EditorAppearanceDropdownComponentProps = {
	contentId: string;
	isDisabled: boolean;
	isStatusDisconnected?: boolean;
	setIsAppearanceDropdownOpen?: (isOpen: boolean) => void;
	isAppearanceDropdownOpen?: boolean;
	onAppearanceUpdateSuccess?: (appearance: ContentAppearanceType) => void;
};

const analyticsContextData = {
	packageName: '@confluence/editor-appearance-button',
	packageVersion: '0.0.1',
};

export const EditorAppearanceDropdownComponent = ({
	contentAppearanceDraft,
	contentId,
	updateContentAppearanceProperty,
	collabEditProvider,
	isDisabled,
	isStatusDisconnected,
	setContentAppearance,
	setIsAppearanceDropdownOpen,
	isAppearanceDropdownOpen,
	onAppearanceUpdateSuccess,
}: EditContentAppearanceButtonProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const apolloClient = getApolloClient();
	const [shouldDisableButton, setShouldDisableButton] = useState<boolean>(false);
	const [currentAppearance, setCurrentAppearance] = useState<ContentAppearanceType>(
		contentAppearanceDraft.appearance,
	);

	useEffect(() => {
		setCurrentAppearance(contentAppearanceDraft.appearance);
	}, [contentAppearanceDraft.appearance]);

	const triggerAnalyticsEvent = useCallback(
		(eventName: string, extraData = {}) => {
			const analyticDetails = {
				contentId,
				...extraData,
			};

			createAnalyticsEvent!({
				type: 'sendTrackEvent',
				data: {
					action: eventName,
					actionSubject: 'updateContentAppearanceDraft',
					source: 'EditContentAppearanceButtonComponent',
					attributes: analyticDetails,
				},
			}).fire();
		},
		[contentId, createAnalyticsEvent],
	);

	const getToggleWidthParams = useCallback((): UpdateContentAppearancePropertyBody => {
		const newVersion = contentAppearanceDraft.version ? contentAppearanceDraft.version + 1 : 1;

		return {
			contentId: contentId || '',
			propertyKey: ContentAppearancePropertyKey.DRAFT,
			propertyValue: currentAppearance,
			versionNumber: newVersion,
		};
	}, [contentAppearanceDraft.version, contentId, currentAppearance]);

	const toggleWidth = useCallback(
		async (newAppearance: ContentAppearanceType) => {
			if (contentId) {
				// Disable button to prevent clicks on it while mutation is occurring to prevent 409s
				setShouldDisableButton(true);
				setCurrentAppearance(newAppearance);

				let params = getToggleWidthParams();
				params = await updateContentAppearancePropertyWithRetry(
					updateContentAppearanceProperty,
					{ ...params, propertyValue: newAppearance },
					apolloClient,
					collabEditProvider,
				);

				setShouldDisableButton(false);

				triggerAnalyticsEvent('content.appearance.draft', {
					appearance: params.propertyValue,
					previousAppearance: contentAppearanceDraft.appearance,
					appearanceVersion: params.versionNumber,
				});

				onAppearanceUpdateSuccess?.(params.propertyValue);
			}
		},
		[
			contentId,
			getToggleWidthParams,
			updateContentAppearanceProperty,
			apolloClient,
			collabEditProvider,
			triggerAnalyticsEvent,
			contentAppearanceDraft.appearance,
			onAppearanceUpdateSuccess,
		],
	);

	return (
		<AnalyticsContext data={analyticsContextData}>
			<ContentAppearanceButton
				userOffline={isStatusDisconnected}
				apolloClient={apolloClient}
				params={getToggleWidthParams()}
				collabEditProvider={collabEditProvider}
				currentContentAppearance={contentAppearanceDraft}
				setContentAppearance={setContentAppearance}
			>
				<DropdownMenu
					trigger={({ triggerRef, ...triggerProps }) => (
						<Tooltip content={intl.formatMessage(messages.adjustWidthButtonLabel)}>
							<Button
								{...triggerProps}
								ref={triggerRef}
								appearance="subtle"
								spacing="default"
								isDisabled={
									shouldDisableButton ||
									// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
									(!Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__) && isDisabled)
								}
								aria-label={intl.formatMessage(messages.adjustWidthButtonLabel)}
							>
								<Flex
									gap="space.050"
									alignItems="center"
									xcss={styles.dropdownButtonFlexGroupStyles}
								>
									<GrowHorizontalIcon label="" aria-hidden="true" size="medium" />
									<ChevronDownIcon label="" aria-hidden="true" size="small" />
								</Flex>
							</Button>
						</Tooltip>
					)}
					placement="bottom-start"
					onOpenChange={(onOpenChangeArgs) => {
						setIsAppearanceDropdownOpen?.(onOpenChangeArgs.isOpen);
					}}
					isOpen={isAppearanceDropdownOpen}
				>
					<DropdownItemGroup>
						<DropdownItem
							onClick={() => toggleWidth(ContentAppearanceType.DEFAULT)}
							isSelected={currentAppearance === ContentAppearanceType.DEFAULT}
						>
							{intl.formatMessage(messages.fullPageLabel)}
						</DropdownItem>
						<DropdownItem
							onClick={() => toggleWidth(ContentAppearanceType.FULL_WIDTH)}
							isSelected={currentAppearance === ContentAppearanceType.FULL_WIDTH}
						>
							{intl.formatMessage(messages.fullWidthLabel)}
						</DropdownItem>
						<DropdownItem
							onClick={() => toggleWidth(ContentAppearanceType.MAX)}
							isSelected={currentAppearance === ContentAppearanceType.MAX}
						>
							{intl.formatMessage(messages.maxLabel)}
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			</ContentAppearanceButton>
		</AnalyticsContext>
	);
};
